import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link, useHistory, useLocation, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import { useMetaTags } from '../../services/useMetaTags';
import trackPageView from '../../services/trackPageView';
import AdvertiseBlock from '../../components/AdvertiseBlock';
import NotFoundPage from '../Errors/NotFound';
import '../../assets/styles/trainingCourse/mainCourse.scss';
import '../../assets/styles/trainingCourse/courseTest.scss';
import '../../assets/styles/trainingCourse/modules.scss';

function resolveLocalePrefix(pathname) {
    const match = pathname.match(/^\/(en|uk)(?=\/|$)/);

    return match ? `/${match[1]}` : '';
}

function withLocalePrefix(path, localePrefix) {
    if (!path) {
        return path;
    }

    if (!localePrefix || /^https?:\/\//.test(path)) {
        return path;
    }

    if (path === localePrefix || path.startsWith(`${localePrefix}/`)) {
        return path;
    }

    return `${localePrefix}${path.startsWith('/') ? path : `/${path}`}`;
}

function normalizeUrl(value) {
    if (!value) {
        return '/img/fallback-min.png';
    }

    if (/^(https?:)?\/\//.test(value)) {
        return value;
    }

    return value.startsWith('/') ? value : `/${value}`;
}

function parseBackendDate(value) {
    const raw = typeof value === 'string' ? value : value?.date;
    if (!raw) {
        return null;
    }

    const normalized = raw.replace(/-/g, '/').replace(/\.\d{6}$/g, '') + ' GMT+0000';
    const date = new Date(normalized);

    if (Number.isNaN(date.getTime())) {
        const fallbackDate = new Date(raw);

        return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
    }

    return date;
}

function formatClock(totalSeconds) {
    const safeSeconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function formatDuration(startDate, endDate) {
    if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
        return '00:00:00';
    }

    const diffSeconds = Math.max(0, Math.floor((endDate.getTime() - startDate.getTime()) / 1000));
    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);
    const seconds = diffSeconds % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function shuffleAnswers(answers) {
    const list = Array.isArray(answers) ? [...answers] : [];

    for (let index = list.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        const current = list[index];
        list[index] = list[randomIndex];
        list[randomIndex] = current;
    }

    return list;
}

function reorderAnswersByPrevious(answers, previousAnswers) {
    const currentAnswers = Array.isArray(answers) ? answers : [];
    const previousOrder = Array.isArray(previousAnswers) ? previousAnswers.map((answer) => String(answer.id)) : [];
    if (!previousOrder.length) {
        return shuffleAnswers(currentAnswers);
    }

    const byId = new Map(currentAnswers.map((answer) => [String(answer.id), answer]));
    const ordered = [];

    previousOrder.forEach((answerId) => {
        if (byId.has(answerId)) {
            ordered.push(byId.get(answerId));
            byId.delete(answerId);
        }
    });

    return [...ordered, ...byId.values()];
}

function isAnsweredFlag(value) {
    return value === true || value === 1 || value === '1' || value === 'true';
}

function mapQuestionsById(questions) {
    const map = new Map();
    (questions || []).forEach((question) => {
        map.set(String(question.id), question);
    });

    return map;
}

function isQuestionPassed(question) {
    if (!Array.isArray(question?.userAnswers)) {
        return false;
    }

    const correctAnswerIds = Array.isArray(question.correctAnswerIds)
        ? question.correctAnswerIds.map((id) => String(id))
        : [];

    const userAnswerIds = question.userAnswers.map((answer) => String(answer.id));

    return userAnswerIds.length === correctAnswerIds.length
        && userAnswerIds.every((answerId) => correctAnswerIds.includes(answerId));
}

function normalizeTestData(testData, previousTestData) {
    if (!testData || !Array.isArray(testData.questions)) {
        return testData;
    }

    const previousQuestions = mapQuestionsById(previousTestData?.questions || []);

    return {
        ...testData,
        questions: testData.questions.map((question) => {
            const previousQuestion = previousQuestions.get(String(question.id));

            return {
                ...question,
                answers: reorderAnswersByPrevious(question.answers, previousQuestion?.answers),
            };
        }),
    };
}

function normalizeExamData(examData, previousExamData) {
    if (!examData || !Array.isArray(examData.questions)) {
        return examData;
    }

    const previousQuestions = mapQuestionsById(previousExamData?.questions || []);

    return {
        ...examData,
        questions: examData.questions.map((question) => {
            const previousQuestion = previousQuestions.get(String(question.id));

            return {
                ...question,
                answers: reorderAnswersByPrevious(question.answers, previousQuestion?.answers),
            };
        }),
    };
}

function normalizeCourseData(courseData, previousCourseData) {
    if (!courseData) {
        return null;
    }

    return {
        ...courseData,
        examData: normalizeExamData(courseData.examData, previousCourseData?.examData),
    };
}

function getActiveElementId(modules, isExamCompletedThisSession) {
    return (modules || []).reduce((accumulator, module) => {
        if (accumulator !== null) {
            return accumulator;
        }

        const nextActive = (module.elements || []).reduce((moduleAccumulator, element) => {
            if (moduleAccumulator !== null) {
                return moduleAccumulator;
            }

            if (isExamCompletedThisSession) {
                return element.id;
            }

            return element.available && !element.completed ? element.id : null;
        }, null);

        return nextActive;
    }, null);
}

function findModuleByElementId(modules, elementId) {
    return (modules || []).find((module) => (module.elements || []).some((element) => element.id === elementId)) || null;
}

function findElementById(modules, elementId) {
    for (let moduleIndex = 0; moduleIndex < (modules || []).length; moduleIndex += 1) {
        const module = modules[moduleIndex];
        const element = (module.elements || []).find((item) => item.id === elementId);

        if (element) {
            return {module, element};
        }
    }

    return null;
}

function buildAnswerRequestBody(answerIds, questionId) {
    const body = new URLSearchParams();

    (answerIds || []).forEach((answerId) => {
        body.append('answer[]', String(answerId));
    });

    body.append('questionId', String(questionId));

    return body;
}

function parseAuthors(authors) {
    return (authors || []).map((author) => {
        const firstName = author?.firstName || '';
        const middleName = author?.middleName || '';
        const lastName = author?.lastName || '';

        return [firstName, middleName, lastName].filter(Boolean).join(' ').trim();
    }).filter(Boolean).join(', ');
}

function buildPreviewPath(isTesting, courseId) {
    return isTesting ? `/testings/preview/${courseId}` : `/course/preview/${courseId}`;
}

function ScoreInline({prices, t}) {
    const items = Array.isArray(prices) ? prices : [];

    return (
        <div className="score-inline">
            {items.map((price, index) => (
                <span className={`score score-${price.code || price.currencyCode}`} key={`${price.code || price.currencyCode}-${index}`}>
                    {price.price ?? price.amount}
                    <span>{t(`app.currencies.${price.code || price.currencyCode}`)}</span>
                </span>
            ))}
        </div>
    );
}

export default function TrainingCourseProgressPage({isTesting = false}) {
    const {id} = useParams();
    const {t} = useTranslation();
    const history = useHistory();
    const {pathname} = useLocation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [activeElement, setActiveElement] = useState(null);
    const [elementsById, setElementsById] = useState({});
    const [activeTab, setActiveTab] = useState('modules');
    const [openModules, setOpenModules] = useState({});
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loadingElementId, setLoadingElementId] = useState(null);
    const [skippedTests, setSkippedTests] = useState({});
    const [skippedExamIds, setSkippedExamIds] = useState([]);
    const [examStartedInSession, setExamStartedInSession] = useState(false);
    const [timeLeftSeconds, setTimeLeftSeconds] = useState(null);
    const shownTestQuestionsRef = useRef(new Set());
    const shownExamQuestionsRef = useRef(new Set());
    const examTimeoutSentRef = useRef(false);

    const localePrefix = useMemo(() => resolveLocalePrefix(pathname), [pathname]);
    const endpoint = isTesting ? `/api/v1/testings/${id}` : `/api/v1/courses/${id}`;
    const textNamespace = isTesting ? 'testings' : 'courses';

    const tr = useCallback((path, fallbackPath, defaultValue = '') => {
        const fullPath = `${textNamespace}.${path}`;
        const fallbackValue = fallbackPath ? t(fallbackPath, defaultValue) : defaultValue;

        return t(fullPath, fallbackValue);
    }, [t, textNamespace]);

    const setCourseData = useCallback((updater) => {
        setData((previous) => {
            if (!previous?.courseData) {
                return previous;
            }

            const nextCourseData = typeof updater === 'function' ? updater(previous.courseData) : updater;

            return {
                ...previous,
                courseData: nextCourseData,
            };
        });
    }, []);

    const loadData = useCallback((options = {}) => {
        const {activateDefaultElement = false} = options;

        setLoading(true);
        setNotFound(false);

        fetch(endpoint, {credentials: 'same-origin'})
            .then(async (response) => {
                if (response.status === 401) {
                    history.replace(withLocalePrefix('/account/login', localePrefix));

                    return null;
                }

                if (response.status === 404) {
                    setNotFound(true);

                    return null;
                }

                const payload = await response.json().catch(() => null);

                if (payload?.state === 'not_found') {
                    setNotFound(true);

                    return null;
                }

                if (response?.status === 409 && payload?.redirectUrl) {
                    history.replace(withLocalePrefix(payload.redirectUrl, localePrefix));

                    return null;
                }

                if (!response.ok) {
                    throw new Error('Failed to load course progress data');
                }

                return payload;
            })
            .then((payload) => {
                if (!payload) {
                    setData(null);

                    return;
                }

                setData((previous) => ({
                    ...payload,
                    courseData: normalizeCourseData(payload.courseData, previous?.courseData),
                }));
                setElementsById({});
                setSkippedTests({});
                setSkippedExamIds([]);
                shownTestQuestionsRef.current = new Set();
                shownExamQuestionsRef.current = new Set();

                if (activateDefaultElement && !isTesting) {
                    const payloadModules = Array.isArray(payload?.courseData?.modules) ? payload.courseData.modules : [];
                    const defaultElementId = getActiveElementId(payloadModules, false);

                    if (defaultElementId) {
                        const found = findElementById(payloadModules, defaultElementId);
                        const activeModule = findModuleByElementId(payloadModules, defaultElementId);

                        setActiveElement({type: found?.element?.type || 'lecture', id: defaultElementId});
                        if (activeModule) {
                            setOpenModules((previous) => ({...previous, [activeModule.id]: true}));
                        }
                    } else if (payload?.courseData?.examData) {
                        setActiveElement({type: 'exam', id: null});
                    } else {
                        setActiveElement(null);
                    }
                }
            })
            .catch(() => {
                setData(null);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [endpoint, history, isTesting, localePrefix]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const course = data?.course || {};
    const courseData = data?.courseData || null;
    const modules = useMemo(() => (Array.isArray(courseData?.modules) ? courseData.modules : []), [courseData?.modules]);
    const examData = courseData?.examData || null;
    const authorsText = useMemo(() => parseAuthors(courseData?.authors || []), [courseData?.authors]);

    const isExamCompleted = Boolean(examData?.dateFinished) && Boolean(examData?.isPassed);
    const isExamCompletedThisSession = Boolean(examData?.dateFinished) && !examStartedInSession;
    const isExamInProgress = Boolean(examData?.dateStarted) && !examData?.dateFinished;

    const completedElementsCount = modules.reduce((accumulator, module) => accumulator + Number(module.completedElementsCount || 0), 0);
    const totalElementsCount = modules.reduce((accumulator, module) => accumulator + (module.elements || []).length, 0);
    const courseCompletenessWithoutExam = totalElementsCount ? (completedElementsCount / totalElementsCount) * 100 : 0;
    const courseCompleteness = (totalElementsCount + 1) ? ((completedElementsCount + (isExamCompleted ? 1 : 0)) / (totalElementsCount + 1)) * 100 : 0;

    const pageTitle = course?.name || (isTesting ? t('testings.title', 'Testings') : t('courses.title'));

    useMetaTags({
        title: pageTitle,
        description: t('app.description'),
        metas: [{name: 'keywords', content: t('app.keywords')}],
    });

    useEffect(() => {
        if (course?.name) {
            trackPageView(course.name);
        }
    }, [course?.name]);

    useEffect(() => {
        if (!courseData) {
            return;
        }

        if (isTesting) {
            setActiveElement({type: 'exam', id: null});
            setActiveTab('modules');

            return;
        }

        const defaultElementId = getActiveElementId(modules, isExamCompletedThisSession);
        if (!activeElement && defaultElementId) {
            const found = findElementById(modules, defaultElementId);
            setActiveElement({type: found?.element?.type || 'lecture', id: defaultElementId});
            const activeModule = findModuleByElementId(modules, defaultElementId);
            if (activeModule) {
                setOpenModules((previous) => ({...previous, [activeModule.id]: true}));
            }
        }
    }, [courseData, isTesting, modules, activeElement, isExamCompletedThisSession]);

    useEffect(() => {
        if (isTesting || !courseData) {
            return;
        }

        const shouldOpenExam = Math.floor(courseCompletenessWithoutExam) >= 100
            && (!isExamCompleted || isExamInProgress || examStartedInSession);

        if (shouldOpenExam && activeElement?.type !== 'exam') {
            setActiveElement({type: 'exam', id: null});
            setActiveTab('modules');
        }
    }, [courseData, isTesting, courseCompletenessWithoutExam, isExamCompleted, isExamInProgress, examStartedInSession, activeElement?.type]);

    const loadElement = useCallback((elementId, elementType) => {
        if (!elementId) {
            return;
        }

        setLoadingElementId(elementId);

        fetch(`/api/v1/course/element/${elementId}`, {credentials: 'same-origin'})
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error('Failed to load element');
                }

                return response.json();
            })
            .then((payload) => {
                setElementsById((previous) => {
                    const previousElement = previous[elementId];
                    const nextElement = elementType === 'test'
                        ? normalizeTestData(payload, previousElement)
                        : payload;

                    return {
                        ...previous,
                        [elementId]: nextElement,
                    };
                });
            })
            .catch(() => null)
            .finally(() => {
                setLoadingElementId(null);
            });
    }, []);

    useEffect(() => {
        if (!activeElement?.id || activeElement.type === 'exam') {
            return;
        }

        if (!elementsById[activeElement.id]) {
            loadElement(activeElement.id, activeElement.type);
        }
    }, [activeElement, elementsById, loadElement]);

    const postExamTimeout = useCallback(() => {
        if (!examData?.courseId) {
            return;
        }

        fetch(`/api/v1/course/exam/${examData.courseId}/timeout`, {
            method: 'POST',
            credentials: 'same-origin',
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error('Failed to complete exam by timeout');
                }

                return response.json();
            })
            .then((payload) => {
                setCourseData((previous) => ({
                    ...previous,
                    examData: normalizeExamData(payload, previous.examData),
                }));
                setSkippedExamIds([]);
            })
            .catch(() => null);
    }, [examData?.courseId, setCourseData]);

    useEffect(() => {
        if (activeElement?.type !== 'exam' || !examData?.dateStarted || examData?.dateFinished) {
            setTimeLeftSeconds(null);

            return undefined;
        }

        const shouldFinishAtDate = parseBackendDate(examData.shouldFinishAtDate);
        const currentDate = parseBackendDate(examData.currentDate) || new Date();

        if (!(shouldFinishAtDate instanceof Date)) {
            setTimeLeftSeconds(null);

            return undefined;
        }

        const initialSeconds = Math.floor((shouldFinishAtDate.getTime() - currentDate.getTime()) / 1000);
        setTimeLeftSeconds(initialSeconds);
        examTimeoutSentRef.current = false;

        const timer = setInterval(() => {
            setTimeLeftSeconds((previous) => {
                const next = Number(previous) - 1;

                if (next <= 0) {
                    if (!examTimeoutSentRef.current) {
                        examTimeoutSentRef.current = true;
                        postExamTimeout();
                    }

                    return 0;
                }

                return next;
            });
        }, 1000);

        return () => {
            clearInterval(timer);
        };
    }, [activeElement?.type, examData?.dateStarted, examData?.dateFinished, examData?.shouldFinishAtDate, examData?.currentDate, postExamTimeout]);

    const activateElement = useCallback((module, element) => {
        if (!element || !module) {
            return;
        }

        const canActivate = Boolean(module.available)
            && Boolean(element.available)
            && !isExamInProgress;

        if (!canActivate) {
            return;
        }

        setActiveElement({id: element.id, type: element.type});
        setActiveTab('modules');

        if (!elementsById[element.id]) {
            loadElement(element.id, element.type);
        }
    }, [isExamInProgress, elementsById, loadElement]);

    const markLectureDone = useCallback((elementId) => {
        fetch(`/api/v1/course/element/${elementId}`, {
            method: 'POST',
            credentials: 'same-origin',
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error('Failed to complete lecture');
                }

                return response.json();
            })
            .then((payload) => {
                setCourseData((previous) => normalizeCourseData(payload, previous));
                setElementsById({});
                setSkippedTests({});
                setSkippedExamIds([]);

                if (!isTesting) {
                    const payloadModules = Array.isArray(payload?.modules) ? payload.modules : [];
                    const nextElementId = getActiveElementId(payloadModules, false);

                    if (nextElementId) {
                        const found = findElementById(payloadModules, nextElementId);
                        const activeModule = findModuleByElementId(payloadModules, nextElementId);

                        setActiveElement({type: found?.element?.type || 'lecture', id: nextElementId});
                        if (activeModule) {
                            setOpenModules((previous) => ({...previous, [activeModule.id]: true}));
                        }
                    } else if (payload?.examData) {
                        setActiveElement({type: 'exam', id: null});
                    }
                }
            })
            .catch(() => null);
    }, [isTesting, setCourseData]);

    const startTest = useCallback((testId) => {
        fetch(`/api/v1/course/test/${testId}`, {
            method: 'POST',
            credentials: 'same-origin',
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error('Failed to start test');
                }

                return response.json();
            })
            .then((payload) => {
                setElementsById((previous) => ({
                    ...previous,
                    [testId]: normalizeTestData(payload, previous[testId]),
                }));
                setSkippedTests((previous) => ({
                    ...previous,
                    [testId]: [],
                }));
            })
            .catch(() => null);
    }, []);

    const submitTestAnswer = useCallback((testId, questionId, answerIds) => {
        const body = buildAnswerRequestBody(answerIds, questionId);

        return fetch(`/api/v1/course/test/${testId}/answer`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            body: body.toString(),
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error('Failed to submit test answer');
                }

                return response.json();
            })
            .then((payload) => {
                setElementsById((previous) => ({
                    ...previous,
                    [testId]: normalizeTestData(payload, previous[testId]),
                }));
            })
            .catch(() => null);
    }, []);

    const startOrRestartExam = useCallback(() => {
        if (!courseData?.id) {
            return;
        }

        fetch(`/api/v1/course/${courseData.id}/exam`, {
            method: 'POST',
            credentials: 'same-origin',
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error('Failed to start exam');
                }

                return response.json();
            })
            .then((payload) => {
                setExamStartedInSession(true);
                setCourseData((previous) => normalizeCourseData(payload, previous));
                setSkippedExamIds([]);
                setActiveElement({type: 'exam', id: null});
            })
            .catch(() => null);
    }, [courseData?.id, setCourseData]);

    const submitExamAnswer = useCallback((questionId, answerIds) => {
        const body = buildAnswerRequestBody(answerIds, questionId);

        return fetch(`/api/v1/course/exam/${questionId}/answer`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            body: body.toString(),
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error('Failed to submit exam answer');
                }

                return response.json();
            })
            .then((payload) => {
                setCourseData((previous) => ({
                    ...previous,
                    examData: normalizeExamData(payload, previous.examData),
                }));
            })
            .catch(() => null);
    }, [setCourseData]);

    const activeElementData = activeElement?.type === 'test' && activeElement?.id
        ? elementsById[activeElement.id]
        : activeElement?.type === 'lecture' && activeElement?.id
            ? elementsById[activeElement.id]
            : null;

    const activeTestData = activeElement?.type === 'test' ? activeElementData : null;
    const activeLectureData = activeElement?.type === 'lecture' ? activeElementData : null;

    const skippedTestSet = useMemo(() => {
        const skippedTestIds = activeTestData?.id ? (skippedTests[activeTestData.id] || []) : [];

        return new Set(skippedTestIds.map((questionId) => String(questionId)));
    }, [activeTestData?.id, skippedTests]);

    const currentTestQuestionIndex = useMemo(() => {
        if (!activeTestData || !Array.isArray(activeTestData.questions)) {
            return -1;
        }

        if (activeTestData.dateFinished) {
            return -1;
        }

        const unresolvedQuestionIndex = activeTestData.questions.findIndex((question) => (
            !isAnsweredFlag(question?.hasAnswer) && !skippedTestSet.has(String(question.id))
        ));

        if (unresolvedQuestionIndex >= 0) {
            return unresolvedQuestionIndex;
        }

        return activeTestData.questions.findIndex((question) => !isAnsweredFlag(question?.hasAnswer));
    }, [activeTestData, skippedTestSet]);

    const currentTestQuestion = currentTestQuestionIndex >= 0 && activeTestData?.questions
        ? activeTestData.questions[currentTestQuestionIndex]
        : null;

    useEffect(() => {
        if (!activeTestData?.id || !currentTestQuestion?.id) {
            return;
        }

        if (!isAnsweredFlag(currentTestQuestion?.hasAnswer)) {
            return;
        }

        setSkippedTests((previous) => {
            const key = activeTestData.id;
            const existing = previous[key] || [];
            if (existing.includes(currentTestQuestion.id)) {
                return previous;
            }

            return {
                ...previous,
                [key]: [...existing, currentTestQuestion.id],
            };
        });
    }, [activeTestData?.id, currentTestQuestion?.id, currentTestQuestion?.hasAnswer]);

    const skippedExamSet = useMemo(() => new Set(skippedExamIds.map((questionId) => String(questionId))), [skippedExamIds]);
    const currentExamQuestionIndex = useMemo(() => {
        if (!examData || !Array.isArray(examData.questions)) {
            return -1;
        }

        const unresolvedQuestionIndex = examData.questions.findIndex((question) => (
            question.isCorrect === null && !skippedExamSet.has(String(question.id))
        ));

        if (unresolvedQuestionIndex >= 0) {
            return unresolvedQuestionIndex;
        }

        return examData.questions.findIndex((question) => question.isCorrect === null);
    }, [examData, skippedExamSet]);

    const currentExamQuestion = currentExamQuestionIndex >= 0 && Array.isArray(examData?.questions)
        ? examData.questions[currentExamQuestionIndex]
        : null;

    useEffect(() => {
        if (activeElement?.type !== 'test' || !currentTestQuestion?.userQuestionId || currentTestQuestion?.wasShown) {
            return;
        }

        const userQuestionId = String(currentTestQuestion.userQuestionId);
        if (shownTestQuestionsRef.current.has(userQuestionId)) {
            return;
        }

        shownTestQuestionsRef.current.add(userQuestionId);
        fetch(`/api/v1/course/test/question/${userQuestionId}/setShown`, {
            method: 'POST',
            credentials: 'same-origin',
        }).catch(() => null);
    }, [activeElement?.type, currentTestQuestion?.userQuestionId, currentTestQuestion?.wasShown]);

    useEffect(() => {
        if (activeElement?.type !== 'exam' || !currentExamQuestion?.id || currentExamQuestion?.wasShown) {
            return;
        }

        const userQuestionId = String(currentExamQuestion.id);
        if (shownExamQuestionsRef.current.has(userQuestionId)) {
            return;
        }

        shownExamQuestionsRef.current.add(userQuestionId);
        fetch(`/api/v1/course/exam/question/${userQuestionId}/setShown`, {
            method: 'POST',
            credentials: 'same-origin',
        }).catch(() => null);
    }, [activeElement?.type, currentExamQuestion?.id, currentExamQuestion?.wasShown]);

    if (loading) {
        return null;
    }

    if (notFound) {
        return <NotFoundPage />;
    }

    if (!data || !courseData) {
        return (
            <section>
                <div className="main-container">
                    <div className="course-section">
                        <h6>{t('app.errors.errorOccurred', 'An error occurred')}</h6>
                    </div>
                </div>
            </section>
        );
    }

    const listPath = withLocalePrefix(data?.actions?.myListUrl || (isTesting ? '/my-testings' : '/mycourses'), localePrefix);
    const previewPath = withLocalePrefix(buildPreviewPath(isTesting, courseData.id), localePrefix);
    const sponsorsTitle = course.developersTitle || tr('preview.sponsorsTitle', 'courses.preview.sponsorsTitle');
    const complexityValue = Number(courseData.complexity || 1);
    const complexityLabel = tr(
        `preview.complexity.${complexityValue === 1 ? 'low' : complexityValue === 2 ? 'medium' : 'high'}`,
        `courses.preview.complexity.${complexityValue === 1 ? 'low' : complexityValue === 2 ? 'medium' : 'high'}`,
    );

    const isCourseCompleted = Math.floor(courseCompleteness) === 100;

    const renderCourseSidebar = !isTesting && (
        <>
            <button
                className={`show-menu-module ${sidebarOpen ? 'close-button' : ''}`}
                type="button"
                onClick={() => setSidebarOpen((previous) => !previous)}>
                <div>
                    <div>
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                    <div>
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
            </button>

            <div className={`course-architecture ${sidebarOpen ? 'open' : ''}`}>
                <div className="course-preview">
                    <div className="img-wrap">
                        <div className="progress-course">
                            <div
                                className={`progress-course-wrap ${isCourseCompleted ? 'completed-course' : ''}`}
                                style={{
                                    width: `${Math.floor(courseCompleteness)}%`,
                                    background: isCourseCompleted ? undefined : 'transparent',
                                }}>
                                <i className="icon-done"></i>
                            </div>
                            <div className="progress-course-info">
                                <h2>
                                    {Math.floor(courseCompleteness)}
                                    <span>%</span>
                                </h2>
                                <h3>{tr('item.completeness', 'courses.item.completeness')}</h3>
                            </div>
                        </div>
                        <img src={normalizeUrl(courseData.image)} className="img-fill" alt="" />
                        <h4>{courseData.title}</h4>
                    </div>
                </div>

                <div className="course-modules js-tabs">
                    <ul className="tabs">
                        <li
                            className={activeTab === 'modules' ? 'active' : ''}
                            onClick={() => setActiveTab('modules')}>
                            {tr('item.modules', 'courses.item.modules')}
                        </li>
                        <li
                            className={activeTab === 'about' ? 'active' : ''}
                            onClick={() => setActiveTab('about')}>
                            {tr('item.about', 'courses.item.about')}
                        </li>
                    </ul>

                    {activeTab === 'modules' && (
                        <div className="tab-panel module-scroll" style={{display: 'block'}}>
                            {modules.map((module) => {
                                const moduleCompleteness = Math.floor(Number(module.completenessPercents || 0));
                                const isModuleCompleted = moduleCompleteness >= 100;
                                const isActiveModule = activeElement?.id
                                    ? (module.elements || []).some((element) => element.id === activeElement.id)
                                    : false;
                                const isModuleOpen = Boolean(openModules[module.id]) || isActiveModule;

                                return (
                                    <div className="accordion-card" key={`module-${module.id}`}>
                                        <div
                                            className={`accordion-toggle ${isModuleOpen ? 'open active' : ''} ${isModuleCompleted ? 'completed' : ''}`}
                                            onClick={() => setOpenModules((previous) => ({...previous, [module.id]: !isModuleOpen}))}>
                                            <div className="module-row">
                                                <div className="module-title">{module.name}</div>
                                                <div className="module-status">
                                                    {isModuleCompleted ? (
                                                        <div className="completed-status"></div>
                                                    ) : module.available ? (
                                                        <div className={`progress-circle module-progress right percent-${moduleCompleteness}`}>
                                                            <div className="slice">
                                                                <div style={{transform: `rotate(${(360 / 100) * moduleCompleteness}deg)`}} className="bar"></div>
                                                                <div className="fill"></div>
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="accordion-content" style={{display: isModuleOpen ? 'block' : 'none'}}>
                                            {(module.elements || []).map((element) => {
                                                const isActive = activeElement?.id === element.id;

                                                return (
                                                    <div
                                                        key={`element-${element.id}`}
                                                        className={`module-section ${isActive ? 'active' : ''}`}
                                                        onClick={() => activateElement(module, element)}>
                                                        <div className="module-row">
                                                            <div className="module-title">
                                                                <h6>{element.name}</h6>
                                                                {element.type === 'test' && (
                                                                    <p>{tr('element.questionCount', 'courses.element.questionCount')} {element.questionsPerTest}</p>
                                                                )}
                                                            </div>
                                                            <div className="module-time">{element.duration} {tr('item.minutes', 'courses.item.minutes')}</div>
                                                            <div className="module-status">
                                                                {element.completed ? (
                                                                    <img src="/img/courses/check.svg" alt="" />
                                                                ) : (!element.available || !module.available || isExamInProgress) ? (
                                                                    <img src="/img/courses/lock.svg" alt="" />
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}

                            <button
                                className="exam"
                                type="button"
                                disabled={Math.floor(courseCompletenessWithoutExam) < 100}
                                onClick={() => {
                                    setActiveElement({type: 'exam', id: null});
                                    setSidebarOpen(false);
                                }}>
                                {tr('item.exam', 'courses.item.exam')}
                            </button>
                        </div>
                    )}

                    {activeTab === 'about' && (
                        <div className="about-course tab-panel" style={{display: 'block'}}>
                            <div className="course-block">
                                <div className="flex-row wrap-flex">
                                    <div className="course-info">
                                        <span className={`score show-only-icon icon-level-${complexityValue}`}><span>{complexityLabel}</span></span>
                                        {String(courseData.hasCertificate) === '1' && (
                                            <span className="score show-only-icon icon-certificate"><span>{tr('item.withCertificate', 'courses.item.withCertificate')}</span></span>
                                        )}
                                    </div>
                                    <div className="score-right">
                                        <ScoreInline prices={courseData.prices} t={t} />
                                    </div>
                                </div>
                            </div>
                            <div className="scroll-block">
                                <h6>
                                    {tr('item.duration', 'courses.item.duration')}
                                    <span> {courseData.duration} {tr('item.minutes', 'courses.item.minutes')}</span>
                                </h6>
                                <h6>
                                    {tr('item.authors', 'courses.item.authors')}
                                    <span> {authorsText}</span>
                                </h6>
                                <div dangerouslySetInnerHTML={{__html: courseData.description || ''}}></div>
                                <Link to={previewPath} className="btn btn-primary js-course-button-preview">
                                    {tr('item.infoPage', 'courses.item.infoPage')}
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );

    const renderLecture = activeLectureData && (
        <div>
            {(activeLectureData.video || activeLectureData.presentation) && (
                <div className="course-iframe">
                    {activeLectureData.video && (
                        <iframe
                            title="course-video"
                            className="js-video-frame"
                            src={`${activeLectureData.video}?&modestbranding=1`}
                            frameBorder="0"></iframe>
                    )}
                    {!activeLectureData.video && activeLectureData.presentation && (
                        <iframe
                            title="course-presentation"
                            src={`https://drive.google.com/viewerng/viewer?embedded=true&url=${window.location.origin}/${activeLectureData.presentation}`}
                            frameBorder="0"></iframe>
                    )}
                </div>
            )}

            <h2>{activeLectureData.name}</h2>
            <p dangerouslySetInnerHTML={{__html: activeLectureData.text || ''}}></p>

            {!activeLectureData.completed && (
                <div className="mark-done">
                    <button type="button" onClick={() => markLectureDone(activeLectureData.id)}>
                        {tr('lecture.markAsDone', 'courses.lecture.markAsDone')}
                    </button>
                </div>
            )}
        </div>
    );

    const renderTest = (() => {
        if (!activeTestData) {
            return null;
        }

        if (!activeTestData.isStarted) {
            return (
                <div className="course-test-intro">
                    <h6>{tr('test.learnInfiniteTimes', 'courses.test.learnInfiniteTimes')}</h6>
                    <h2>{tr('test.header', 'courses.test.header')} «{activeTestData.moduleName}»</h2>
                    <p>{tr('test.questionCount', 'courses.test.questionCount')} {activeTestData.questionsCount}</p>
                    <p>
                        {tr('test.maxScoreCaption', 'courses.test.maxScoreCaption')}
                        <ScoreInline prices={activeTestData.prices} t={t} />
                    </p>
                    <p>{tr('test.scoreDependsOnAnswers', 'courses.test.scoreDependsOnAnswers')}</p>
                    <div className="btn-block">
                        <button type="button" className="btn-primary" onClick={() => startTest(activeTestData.id)}>
                            {tr('test.startTest', 'courses.test.startTest')}
                        </button>
                    </div>
                </div>
            );
        }

        if (activeTestData.dateFinished && currentTestQuestionIndex < 0) {
            const correctQuestions = Number(activeTestData.correctQuestions || 0);
            const incorrectQuestions = Number(activeTestData.incorrectQuestions || 0);
            const questionsCount = Number(activeTestData.questionsCount || 1);
            const completenessPercents = Math.floor((correctQuestions / Math.max(1, questionsCount)) * 100);
            const dateStarted = parseBackendDate(activeTestData.dateStarted);
            const dateFinished = parseBackendDate(activeTestData.dateFinished);

            return (
                <div>
                    <h6>{tr('test.header', 'courses.test.header')} «{activeTestData.moduleName}»</h6>
                    <h2>{tr('test.result', 'courses.test.result')}</h2>
                    <div className={`progress-circle percent-${completenessPercents} result-test`}>
                        <span>
                            <strong>{completenessPercents}%</strong>
                            {tr('test.answerCorrectness', 'courses.test.answerCorrectness')}
                        </span>
                        <div className="slice">
                            <div className="bar" style={{transform: `rotate(${(360 / 100) * completenessPercents}deg)`}}></div>
                            <div className="fill"></div>
                        </div>
                    </div>
                    <p>{tr('test.correctAnswers', 'courses.test.correctAnswers')} <strong>{correctQuestions}</strong></p>
                    <p>{tr('test.incorrectAnswers', 'courses.test.incorrectAnswers')} <strong>{incorrectQuestions}</strong></p>
                    <p>{tr('test.timeToComplete', 'courses.test.timeToComplete')} <strong>{formatDuration(dateStarted, dateFinished)}</strong></p>
                    <p>{tr('test.scoreEarned', 'courses.test.scoreEarned')}</p>
                    <div className="score-block-result">
                        <ScoreInline prices={activeTestData.transactions || []} t={t} />
                    </div>
                    <div className="btn-block btn-group-vertical">
                        <button type="button" className="btn-primary" onClick={() => loadData({activateDefaultElement: true})}>
                            {tr('test.nextModule', 'courses.test.nextModule')}
                        </button>
                        <button type="button" className="btn-primary btn-secondary" onClick={() => startTest(activeTestData.id)}>
                            {tr('test.startTest', 'courses.test.startTest')}
                        </button>
                    </div>
                </div>
            );
        }

        if (!currentTestQuestion) {
            return null;
        }

        const hasAnswer = isAnsweredFlag(currentTestQuestion?.hasAnswer);
        const selectedAnswers = Array.isArray(currentTestQuestion.userAnswers)
            ? currentTestQuestion.userAnswers.map((answer) => String(answer.id))
            : [];
        const questionPassed = isQuestionPassed(currentTestQuestion);

        return (
            <div>
                <h6>{tr('test.header', 'courses.test.header')} «{activeTestData.moduleName}»</h6>
                <h2>
                    {tr('test.question', 'courses.test.question')} {currentTestQuestionIndex + 1} {tr('test.from', 'courses.test.from')} {activeTestData.questions.length}
                </h2>

                <div className="question-counter">
                    <ul>
                        {(activeTestData.questions || []).map((question, index) => (
                            <li
                                key={`test-question-counter-${question.id}`}
                                className={`${isAnsweredFlag(question.hasAnswer) ? (isQuestionPassed(question) ? 'passed' : 'not-passed') : ''} ${index === currentTestQuestionIndex ? 'current' : ''}`}>
                                {index + 1}
                            </li>
                        ))}
                    </ul>
                </div>

                <h3>{currentTestQuestion.title}</h3>
                {currentTestQuestion.type === 'checkbox' && (
                    <p className="description">{tr('test.questions.multiselect', 'courses.test.questions.multiselect')}</p>
                )}

                <TestQuestionForm
                    key={`test-form-${currentTestQuestion.id}`}
                    disabled={hasAnswer}
                    inputType={currentTestQuestion.type === 'checkbox' ? 'checkbox' : 'radio'}
                    answers={currentTestQuestion.answers || []}
                    selectedAnswerIds={selectedAnswers}
                    question={currentTestQuestion}
                    onSkip={() => {
                        setSkippedTests((previous) => ({
                            ...previous,
                            [activeTestData.id]: [...(previous[activeTestData.id] || []), currentTestQuestion.id],
                        }));
                    }}
                    onSubmit={(answerIds) => submitTestAnswer(activeTestData.id, currentTestQuestion.id, answerIds)}
                    tr={tr}
                />

                {hasAnswer && !questionPassed && (
                    <>
                        <p className="answer-explanation">
                            <strong>{tr('test.questions.answerExplanation', 'courses.test.questions.answerExplanation')}</strong>{' '}
                            {currentTestQuestion.correctnessDescription}
                        </p>
                        <p
                            className="contact-us"
                            dangerouslySetInnerHTML={{
                                __html: tr('test.questions.contactUsIfQuestionIsWrong', 'courses.test.questions.contactUsIfQuestionIsWrong'),
                            }}></p>
                    </>
                )}
            </div>
        );
    })();

    const renderExam = (() => {
        if (activeElement?.type !== 'exam' || !examData) {
            return null;
        }

        const listRoute = withLocalePrefix(isTesting ? '/testings' : '/courses', localePrefix);

        if (!examData.dateStarted) {
            return (
                <div className="course-test-intro">
                    <h6>{tr('exam.completeConditions', 'courses.exam.completeConditions')}</h6>
                    <h2>{tr('exam.header', 'courses.exam.header')} «{examData.courseName}»</h2>
                    <p>{tr('exam.questionCount', 'courses.exam.questionCount')} {examData.questionsCount}</p>
                    <p>{tr('exam.timeToFinishExam', 'courses.exam.timeToFinishExam')} {examData.duration} {tr('item.minutes', 'courses.item.minutes')}</p>
                    <p>{tr('exam.effortsLeft', 'courses.exam.effortsLeft')} {examData.effortsCount}</p>
                    <p>
                        {tr('exam.maxScoreCaption', 'courses.exam.maxScoreCaption')}
                        <ScoreInline prices={examData.prices || []} t={t} />
                    </p>
                    <p>{tr('exam.scoreDependsOnAnswers', 'courses.exam.scoreDependsOnAnswers')}</p>
                    <div className="btn-block">
                        <button type="button" className="btn-primary" onClick={startOrRestartExam}>
                            {tr('exam.startExam', 'courses.exam.startExam')}
                        </button>
                    </div>
                </div>
            );
        }

        if (examData.dateFinished) {
            const correctQuestions = (examData.questions || []).filter((question) => question.isCorrect).length;
            const incorrectQuestions = Math.max(0, Number(examData.questionsCount || 0) - correctQuestions);
            const completenessPercents = Math.floor((correctQuestions / Math.max(1, Number(examData.questionsCount || 1))) * 100);
            const dateStarted = parseBackendDate(examData.dateStarted);
            const dateFinished = parseBackendDate(examData.dateFinished);
            const effortNumber = Number(examData.effortsForExam || 0) - Number(examData.effortsCount || 0);
            const isEffortsExhausted = Number(examData.effortsForExam || 0) - Number(examData.effortsCount || 0) === Number(examData.effortsForExam || 0);

            return (
                <div>
                    <h6>{tr('exam.header', 'courses.exam.header')} «{examData.courseName}»</h6>
                    <h2>{tr('exam.result', 'courses.exam.result')}</h2>
                    {completenessPercents < 70 && !isEffortsExhausted && (
                        <p>{tr('exam.examFailedCaption', 'courses.exam.examFailedCaption')}</p>
                    )}
                    {completenessPercents >= 70 && (
                        <p>{tr('exam.examPassedCaption', 'courses.exam.examPassedCaption')} "{examData.courseName}"</p>
                    )}
                    <div className={`progress-circle percent-${completenessPercents} result-test`}>
                        <span>
                            <strong>{completenessPercents}%</strong>
                            {tr('exam.answerCorrectness', 'courses.exam.answerCorrectness')}
                        </span>
                        <div className="slice">
                            <div className="bar" style={{transform: `rotate(${(360 / 100) * completenessPercents}deg)`}}></div>
                            <div className="fill"></div>
                        </div>
                    </div>
                    <p>{tr('exam.effortNumber', 'courses.exam.effortNumber')} <strong>{effortNumber} {tr('exam.from', 'courses.exam.from')} {examData.effortsForExam}</strong></p>
                    <p>{tr('exam.correctAnswers', 'courses.exam.correctAnswers')} <strong>{correctQuestions}</strong></p>
                    <p>{tr('exam.incorrectAnswers', 'courses.exam.incorrectAnswers')} <strong>{incorrectQuestions}</strong></p>
                    <p>{tr('exam.timeToComplete', 'courses.exam.timeToComplete')} <strong>{formatDuration(dateStarted, dateFinished)}</strong></p>
                    <p>{tr('exam.scoreEarned', 'courses.exam.scoreEarned')}</p>
                    <div className="score-block-result">
                        <ScoreInline prices={examData.transactions || []} t={t} />
                    </div>
                    <div className="btn-block btn-group-vertical">
                        <button type="button" className="btn-primary" onClick={() => history.push(listRoute)}>
                            {tr('exam.toCourses', 'courses.exam.toCourses')}
                        </button>
                        {completenessPercents >= 70 && (
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={() => history.push(withLocalePrefix(`/course/${examData.courseId}/certificate`, localePrefix))}>
                                {tr('preview.certification.view', 'courses.preview.certification.view')}
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn-primary btn-secondary"
                            disabled={isEffortsExhausted}
                            onClick={startOrRestartExam}>
                            {tr('exam.restartExam', 'courses.exam.restartExam')}
                        </button>
                        {isEffortsExhausted && <p className="error-text">{tr('exam.insufficientEfforts', 'courses.exam.insufficientEfforts')}</p>}
                    </div>
                </div>
            );
        }

        if (!currentExamQuestion) {
            return null;
        }

        return (
            <div>
                <h6>{tr('exam.header', 'courses.exam.header')} «{examData.courseName}»</h6>
                <p className="time-left">{tr('exam.timeLeft', 'courses.exam.timeLeft')} {formatClock(timeLeftSeconds)}</p>
                <h2>{tr('exam.question', 'courses.exam.question')} {currentExamQuestionIndex + 1} {tr('exam.from', 'courses.exam.from')} {examData.questions.length}</h2>

                <div className="question-counter">
                    <ul>
                        {(examData.questions || []).map((question, index) => (
                            <li
                                key={`exam-question-counter-${question.id}`}
                                className={`${question.isCorrect !== null ? (question.isCorrect ? 'passed' : 'not-passed') : ''} ${index === currentExamQuestionIndex ? 'current' : ''}`}>
                                {index + 1}
                            </li>
                        ))}
                    </ul>
                </div>

                <h3>{currentExamQuestion.title}</h3>
                {currentExamQuestion.type === 'checkbox' && (
                    <p className="description">{tr('test.questions.multiselect', 'courses.test.questions.multiselect')}</p>
                )}

                <TestQuestionForm
                    key={`exam-form-${currentExamQuestion.id}`}
                    disabled={false}
                    inputType={currentExamQuestion.type === 'checkbox' ? 'checkbox' : 'radio'}
                    answers={currentExamQuestion.answers || []}
                    selectedAnswerIds={[]}
                    question={currentExamQuestion}
                    onSkip={() => {
                        setSkippedExamIds((previous) => [...previous, currentExamQuestion.id]);
                    }}
                    onSubmit={(answerIds) => submitExamAnswer(currentExamQuestion.id, answerIds)}
                    tr={tr}
                    isExam={true}
                />
            </div>
        );
    })();

    const content = activeElement?.type === 'lecture'
        ? renderLecture
        : activeElement?.type === 'test'
            ? renderTest
            : renderExam;

    return (
        <div>
            <div className="section-bg"></div>
            <section>
                <div className="main-container">
                    <AdvertiseBlock unitId="course1" blockLocation="top" entityId={String(course.id || '')} />

                    <div className="row">
                        <nav className="breadcrumbs">
                            <ul>
                                <li>
                                    <Link to={listPath}>{tr('preview.myCoursesBreadcrumbs', 'courses.preview.myCoursesBreadcrumbs')}</Link>
                                </li>
                                <li className="active" aria-current="page">{course.name}</li>
                            </ul>
                        </nav>
                    </div>

                    <div className="course-grid" style={isTesting ? {display: 'block'} : undefined}>
                        {renderCourseSidebar}

                        <div className="course-content" onClick={() => setSidebarOpen(false)}>
                            <div className="content-wrap">
                                {loadingElementId && activeElement?.id === loadingElementId ? null : content}
                            </div>

                            {!!(course.developers || []).length && (
                                <div className="course-sponsor-block">
                                    <h6>{sponsorsTitle}</h6>
                                    <div className="sponsor-grid">
                                        {(course.developers || []).map((developer, index) => (
                                            <div key={`course-progress-developer-${course.id}-${index}`}>
                                                {developer.imageLink ? (
                                                    <a href={developer.imageLink} target="_blank" rel="noreferrer">
                                                        <img src={normalizeUrl(developer.image)} alt="" />
                                                    </a>
                                                ) : (
                                                    <img src={normalizeUrl(developer.image)} alt="" />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <AdvertiseBlock unitId="course2" blockLocation="bottom" entityId={String(course.id || '')} />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function TestQuestionForm({
    question,
    answers,
    inputType,
    selectedAnswerIds,
    disabled,
    onSubmit,
    onSkip,
    tr,
    isExam = false,
}) {
    const [selected, setSelected] = useState(selectedAnswerIds || []);
    const [pending, setPending] = useState(false);

    useEffect(() => {
        setSelected(selectedAnswerIds || []);
    }, [question?.id]);

    const hasAnswered = isAnsweredFlag(question?.hasAnswer);
    const canSubmit = !pending && !hasAnswered && selected.length > 0;

    const handleChange = (answerId, checked) => {
        const normalizedId = String(answerId);

        if (inputType === 'checkbox') {
            if (checked) {
                setSelected((previous) => [...new Set([...previous, normalizedId])]);
            } else {
                setSelected((previous) => previous.filter((item) => item !== normalizedId));
            }

            return;
        }

        setSelected([normalizedId]);
    };

    const handleAnswerClick = (event, answerId) => {
        event.preventDefault();

        if (hasAnswered || disabled || pending) {
            return;
        }

        handleChange(answerId, !isAnswerSelected(answerId));
    };

    const isAnswerSelected = (answerId) => selected.includes(String(answerId));

    const answerClassName = (answer) => {
        if (!hasAnswered) {
            return '';
        }

        if (isExam) {
            return question.isCorrect === null ? '' : (question.isCorrect ? 'correct-answer' : 'incorrect-answer');
        }

        const isCorrect = Array.isArray(question.correctAnswerIds)
            ? question.correctAnswerIds.map((id) => String(id)).includes(String(answer.id))
            : false;
        const selectedByUser = Array.isArray(question.userAnswers)
            ? question.userAnswers.map((item) => String(item.id)).includes(String(answer.id))
            : false;

        if (!isCorrect && selectedByUser) {
            return 'incorrect-answer';
        }

        return isCorrect ? 'correct-answer' : '';
    };

    return (
        <form className="js-form" onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) {
                return;
            }

            setPending(true);
            Promise.resolve(onSubmit(selected)).finally(() => {
                setPending(false);
            });
        }}>
            <div className="question-block">
                {(answers || []).map((answer, index) => (
                    <div className={`form-group-row ${answerClassName(answer)}`} key={`answer-${question.id}-${answer.id}`}>
                        <input
                            className={`magic-${inputType}`}
                            type={inputType}
                            name="answer[]"
                            value={answer.id}
                            id={`${question.id}-${answer.id}-${index}`}
                            checked={isAnswerSelected(answer.id)}
                            disabled={hasAnswered || disabled}
                            onChange={(event) => handleChange(answer.id, event.target.checked)} />
                        <label
                            htmlFor={`${question.id}-${answer.id}-${index}`}
                            onClick={(event) => handleAnswerClick(event, answer.id)}>
                            {answer.text || answer.title}
                        </label>
                    </div>
                ))}
            </div>

            <div className="btn-block btn-group-vertical">
                {!hasAnswered ? (
                    <>
                        <button type="submit" disabled={!canSubmit} className="btn-primary js-send-answer">
                            {tr('test.questions.sendAnswer', 'courses.test.questions.sendAnswer')}
                        </button>
                        <button type="button" className="btn-primary btn-secondary js-skip-question" onClick={onSkip}>
                            {tr('test.questions.skipQuestion', 'courses.test.questions.skipQuestion')}
                        </button>
                    </>
                ) : (
                    <button type="button" className="btn-primary js-next-question" onClick={onSkip}>
                        {tr('test.questions.nextQuestion', 'courses.test.questions.nextQuestion')}
                    </button>
                )}
            </div>
        </form>
    );
}
